import { AddRounded, ArrowForwardIosRounded, CancelRounded, CheckCircleRounded, CheckRounded, FiberNewRounded, FilterListRounded, HelpOutlineRounded, KeyboardReturnRounded, MopedRounded, PersonSearchRounded, QuestionMarkRounded, RefreshRounded, RunCircleRounded } from "@mui/icons-material";
import { Badge, Box, Button, Chip, Container, IconButton, ListItemIcon, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import { Stack } from "@mui/system";
import { Query, QueryDocumentSnapshot, collection, endBefore, getCountFromServer, getDocs, getFirestore, limit, limitToLast, orderBy, query, startAfter, startAt, where } from "firebase/firestore";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ChangeEvent, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useAppDispatch, useAppSelector } from "../../../store";
import { setPassportPerPage } from "../../../store/slices/appSlice";
import { PassportRequest, PassportRequestStatus, PassportRequestType } from "../../../types/PassportRequest";
import { updatePassportRequest } from "../../../utils/updatePassportRequest";

import passportRequestConverter from "../../../converters/passportRequestConverter";
import { Search, SearchIconWrapper, StyledInputBase } from "../../Search";
import { useNavigate } from "react-router-dom";

const getBadgeIconFromStatus = (status: PassportRequestStatus | 'All') => {
  if (status === 'All') return undefined;
  else if (status === 'Processing') return <RunCircleRounded color='warning' />;
  else if (status === 'Available') return <MopedRounded color='secondary' />;
  else if (status === 'Completed') return <CheckCircleRounded color='success' />;
  else if (status === 'Cancelled') return <CancelRounded color='error' />;
  return <FiberNewRounded color='info' />;
};

export const ChipIcon = (status: PassportRequestStatus) => {
  if (status === 'New')
    return <FiberNewRounded />
  else if (status === 'Processing')
    return <RunCircleRounded />
  else if (status === 'Available')
    return <MopedRounded />
  else if (status === 'Completed')
    return <CheckCircleRounded />
  else if (status === 'Returned')
    return <KeyboardReturnRounded />
  else
    return <CancelRounded />
};

export const ChipLabel = (status: PassportRequestStatus) => {
  if (status === 'New')
    return 'New';
  else if (status === 'Processing')
    return 'Working...';
  else if (status === 'Available')
    return 'Ready for Pickup';
  else if (status === 'Completed')
    return 'Completed';
  else if (status === 'Returned')
    return 'Returned';
  else
    return 'Cancelled';
};

export const ChipColor = (status: PassportRequestStatus) => {
  if (status === 'New')
    return 'info';
  else if (status === 'Processing')
    return 'warning';
  else if (status === 'Available')
    return 'secondary';
  else if (status === 'Completed')
    return 'success';
  else if (status === 'Returned')
    return 'primary';
  else
    return 'error';
};

export const PassportRequestsPage = () => {
  const db = getFirestore();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authState = useAppSelector(s => s.auth);
  const rowsPerPage = useAppSelector((s) => s.app.passportRequestPerPage ?? 25);

  const adminOnly = authState.URB.roles.some((r: string) => ['super', 'admin'].includes(r));
  
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  
  const [firstRow, setFirstRow] = useState<QueryDocumentSnapshot | null>(null);
  const [lastRow, setLastRow] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  
  const [passportRequests, setPassportRequests] = useState<PassportRequest[]>([]);
  const [search, setSearch] = useState('');

  const [type, setType] = useState<PassportRequestType | 'All'>('All');
  const [typeAnchorEl, setTypeAnchorEl] = useState<null | HTMLElement>(null);

  const [filter, setFilter] = useState<PassportRequestStatus | 'All'>('New');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

  const [selected, setSelected] = useState<PassportRequest>();
  const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(null);

  const refresh = (callback?: (q: Query<PassportRequest>) => Query<PassportRequest>) => {
    setLoading(true);
    let q: Query<PassportRequest> = query(collection(db, `PassportRequests`)).withConverter(passportRequestConverter);

    if (search.trim() === '') 
      q = query(q, orderBy('dateRequested', 'asc'));
    else
      q = query(q, where('idNumber', '>=', search), where('idNumber', '<=', search + '\uf8ff'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'asc'));

    if(callback)
      q = callback(q);

    if (type !== 'All')
      q = query(q, where('type', '==', type));

    if (filter !== 'All')
      q = query(q, where('status', '==', filter));
    
    getDocs(q).then(qSnapshot => {
      setPassportRequests(qSnapshot.docs.map(d => d.data()));
      setFirstRow(qSnapshot.docs[0]);
      setLastRow(qSnapshot.docs[qSnapshot.docs.length - 1]);
      setLoading(false);
    });

    // Count query
    let qCount: Query = query(collection(db, `PassportRequests`));
    if (search.trim() === '') 
      qCount = query(qCount, orderBy('dateRequested', 'asc'));
    else
      qCount = query(qCount, where('idNumber', '>=', search), where('idNumber', '<=', search + '\uf8ff'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'asc'));
    
    if (type !== 'All')
      qCount = query(qCount, where('type', '==', type));

    if (filter !== 'All') 
      qCount = query(qCount, where('status', '==', filter));

    getCountFromServer(qCount).then(f => setTotalRows(f.data().count));
  };

  const searchDebouncedCallback = useDebouncedCallback((value) => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, 1000);

  const handleSearchOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
    searchDebouncedCallback(event.currentTarget.value);
  };

  const handleTypeButtonOnClick = (event: React.MouseEvent<HTMLElement>) => {
    setTypeAnchorEl(event.currentTarget);
  };

  const handleTypeMenuOnClose = () => setTypeAnchorEl(null);

  const handleFilterButtonOnClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuOnClose = () => setFilterAnchorEl(null);

  const applyType = (t: PassportRequestType | 'All') => {
    setType(t);
    setTypeAnchorEl(null);
  };
  
  const typeAll = () => applyType('All');
  const typeRenewal = () => applyType('Renewal');
  const typeVacation = () => applyType('Vacation');
  const typeBank = () => applyType('Bank');
  const typeExit = () => applyType('Exit');
  const typeCopy = () => applyType('Copy');

  const applyFilter = (f: PassportRequestStatus | 'All') => {
    setFilter(f);
    setFilterAnchorEl(null);
  }
  
  const filterAll = () => applyFilter('All');
  const filterNew = () => applyFilter('New');
  const filterProcessing = () => applyFilter('Processing');
  const filterAvailable = () => applyFilter('Available');
  const filterCompleted = () => applyFilter('Completed');

  const handlePageOnChange = (_e: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    if (to > page) {
      // Next
      refresh(q => {
        return query(q, startAfter(lastRow), limit(rowsPerPage));
      });
    } else if (to < page) {
      // Previous
      refresh(q => {
        return query(q, endBefore(firstRow), limitToLast(rowsPerPage));
      });
    } else {
      // Same
      refresh(q => {
        return query(q, startAt(firstRow), limit(rowsPerPage));
      });
    }
    setPage(to);
  };

  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPassportPerPage(+event.target.value));
    setPage(0);
  };
  
  const updateStatus = (passportRequest: PassportRequest, status: PassportRequestStatus) => {
    if (adminOnly) {
      if (window.confirm(`Confirm to set status to '${status}'?`)) {
        updatePassportRequest(passportRequest, status).then(() => {
          setPassportRequests(passportRequests.map(p => {
            if (p.id === passportRequest.id) {
              return { ...p, status};
            }
  
            return p;
          }));
          enqueueSnackbar(`Passport request has been set to '${status}'!`, { variant: 'success' });
        });
      }
    } else {
      window.alert('You are not authorized to perform this action!');
    }
  };

  useEffect(() => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, [rowsPerPage, type, filter]);

  return (
    <>
      <Container disableGutters>
        <Typography variant="h5" mt={2} mb={5}>Passport Requests</Typography>
        <Stack direction='row' mb={1}>
          <Box width={650}>
            <Search>
              <SearchIconWrapper>
                <PersonSearchRounded color='primary' />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search employee by Id Number"
                inputProps={{ 'aria-label': 'search' }}
                value={search}
                onChange={handleSearchOnChanged}
              />
            </Search>
          </Box>
          <Box flexGrow={1} />
          {/* <Button startIcon={<AddRounded />} onClick={() => { window.alert('Not yet implemented. :p') }}>Create New Request</Button> */}
        </Stack>
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={76} align="right">Count</TableCell>
                  <TableCell width={120}>Id Number</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell width={150}>
                    <Stack 
                      direction='row' 
                      alignItems='center'
                      sx={{
                        backgroundColor: '#0000001f',
                        borderRadius: 2,
                        borderWidth: 1,
                        borderColor: '#0000004f',
                        borderStyle: 'solid',
                        padding: 1,
                      }}
                    >
                      <Box flexGrow={1}>
                        Type
                      </Box>
                      <Tooltip title='Filter Type'>
                        <IconButton size='small' onClick={handleTypeButtonOnClick}>
                          <Badge 
                            badgeContent={
                              type !== 'All' 
                              ? (
                                <Typography 
                                  component='div' 
                                  variant="caption" 
                                  sx={{ 
                                    backgroundColor: theme => theme.palette.info.main,
                                    paddingX: 0.5,
                                    borderRadius: 1
                                  }}
                                >
                                  {type}
                                </Typography>
                              ) : 0
                            }
                          >
                            <FilterListRounded />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell width={220}>Date Requested</TableCell>
                  <TableCell width={150}>
                    <Stack 
                      direction='row' 
                      alignItems='center'
                      sx={{
                        backgroundColor: '#0000001f',
                        borderRadius: 2,
                        borderWidth: 1,
                        borderColor: '#0000004f',
                        borderStyle: 'solid',
                        padding: 1,
                      }}
                    >
                      <Box flexGrow={1}>
                        Status
                      </Box>
                      <Tooltip title='Filter Status'>
                        <IconButton size='small' onClick={handleFilterButtonOnClick}>
                          <Badge badgeContent={filter !== 'All' ? getBadgeIconFromStatus(filter) : 0}>
                            <FilterListRounded />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  {adminOnly && (
                    <TableCell width={168} align='center'>Actions</TableCell>
                  )}
                  <TableCell width={72} align='center'>
                    <Tooltip title='Refresh'>
                      <IconButton color='info' onClick={() => refresh(q => query(q, limit(rowsPerPage)))}><RefreshRounded /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {passportRequests.map((p, i) => {
                  return (
                    <TableRow 
                      hover
                      key={p.id}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td, &:last-child th': { border: 0 } 
                      }}
                    >
                      <TableCell align="right">{i + 1}</TableCell>
                      <TableCell>{p.idNumber}</TableCell>
                      <TableCell>
                        <span id={p.id}>{p.name}</span>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Typography variant="caption">
                            {p.type}
                          </Typography>
                          {p.type === 'Copy' && (
                            <Tooltip title={p.reason} arrow>
                              <HelpOutlineRounded color="info" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{p.dateRequested ? DateTime.fromISO(p.dateRequested).toLocaleString(DateTime.DATETIME_MED) : ''}</TableCell>
                      <TableCell>
                        <Chip icon={ChipIcon(p.status)} label={ChipLabel(p.status)} color={ChipColor(p.status)} variant="outlined" />
                      </TableCell>
                      {adminOnly && (
                        <TableCell>
                          <Stack direction='row' spacing={1}>
                            <Tooltip title='Set as Processing'>
                              <span>
                                <IconButton 
                                  color='warning' 
                                  disabled={p.status !== 'New'} 
                                  onClick={() => updateStatus(p, 'Processing')}>
                                    <RunCircleRounded />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Set as Ready for Pickup'>
                              <span>
                                <IconButton
                                  color='secondary' 
                                  disabled={p.status !== 'New' && p.status !== 'Processing'} 
                                  onClick={() => updateStatus(p, 'Available')}>
                                    <MopedRounded />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {(((p.type === 'Bank' || p.type === 'Renewal') && (p.status !== 'Completed' && p.status !== 'Returned')) || (p.type !== 'Bank' && p.type !== 'Renewal')) && (
                              <Tooltip title='Set as Completed'>
                                <span>
                                  <IconButton 
                                    color='success' 
                                    disabled={p.status === 'Completed' || p.status === 'Cancelled'} 
                                    onClick={() => updateStatus(p, 'Completed')}>
                                      <CheckRounded />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {(p.type === 'Bank' || p.type === 'Renewal') && (p.status === 'Completed' || p.status === 'Returned' ) && (
                              <Tooltip title='Set as Returned'>
                                <span>
                                  <IconButton 
                                    color='primary' 
                                    disabled={p.status === 'Returned'} 
                                    onClick={() => updateStatus(p, 'Returned')}>
                                      <KeyboardReturnRounded />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                      <TableCell>
                        <Tooltip title='View'>
                          <IconButton color='info' onClick={() => navigate(`/dashboard/passportRequests/${p.id}`)}><ArrowForwardIosRounded /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage ?? 25}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
            }}
            onPageChange={handlePageOnChange}
            onRowsPerPageChange={handleRowsPerPageOnChange}
            ActionsComponent={TablePaginationActions} />
        </Paper>
      </Container>
      <Menu
        anchorEl={typeAnchorEl}
        open={Boolean(typeAnchorEl)}
        onClose={handleTypeMenuOnClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 12,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={typeAll}>
          All
        </MenuItem>
        <MenuItem onClick={typeRenewal}>
          Renewal
        </MenuItem>
        <MenuItem onClick={typeVacation}>
          Vacation
        </MenuItem>
        <MenuItem onClick={typeBank}>
          Bank
        </MenuItem>
        <MenuItem onClick={typeExit}>
          Exit
        </MenuItem>
        <MenuItem onClick={typeCopy}>
          Copy
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterMenuOnClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 12,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={filterAll}>
          <ListItemIcon></ListItemIcon>
          All
        </MenuItem>
        <MenuItem onClick={filterNew}>
          <ListItemIcon>
            <FiberNewRounded color='info' />
          </ListItemIcon>
          New
        </MenuItem>
        <MenuItem onClick={filterProcessing}>
          <ListItemIcon>
            <RunCircleRounded color='warning' />
          </ListItemIcon>
          Processing
        </MenuItem>
        <MenuItem onClick={filterAvailable}>
          <ListItemIcon>
            <MopedRounded color='secondary' />
          </ListItemIcon>
          Available
        </MenuItem>
        <MenuItem onClick={filterCompleted}>
          <ListItemIcon>
            <CheckCircleRounded color='success' />
          </ListItemIcon>
          Completed
        </MenuItem>
      </Menu>
    </>
  );
};